import { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  X,
  Briefcase,
  Layers,
  Workflow,
  DollarSign,
  BookOpen,
  Search as SearchIcon,
  Key,
  User,
  MessageSquare,
  ArrowRight,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { cn, Logo } from "@nexirt/ui";
import StatusPill from "./StatusPill";

/**
 * MobileDrawer — right-side slide-in for ≤md per Navigation.html mobile
 * artboard (Rev 25 Phase 2 Loop F). Replaces the rev-21 inline mobile
 * drop-down menu.
 *
 * 86% viewport width (max 420px), full height. Scrim + body scroll-lock.
 * Open animation 350ms cubic-bezier; reduced-motion disabled.
 */

type NavMe = { label: string };

type NavRow = {
  to: string;
  label: string;
  helper: string;
  icon: LucideIcon;
  badge?: "new";
  hidden?: boolean;
};

const NAV_ROWS: NavRow[] = [
  { to: "/work", label: "Work", helper: "Recent client deliveries", icon: Briefcase },
  { to: "/services", label: "Services", helper: "What we build", icon: Layers },
  { to: "/process", label: "Process", helper: "5-stage delivery model", icon: Workflow },
  { to: "/pricing", label: "Pricing", helper: "Project + Care tiers", icon: DollarSign },
  // TODO(journal): re-enable when /journal content exists.
  { to: "/journal", label: "Journal", helper: "Engineering notes", icon: BookOpen, badge: "new", hidden: true },
];

export function MobileDrawer({
  open,
  onClose,
  authed,
  me,
  onSignOut,
  onOpenChat,
}: {
  open: boolean;
  onClose: () => void;
  authed: boolean;
  me: NavMe | null;
  onSignOut: () => void;
  onOpenChat: () => void;
}) {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Body scroll-lock when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button on open for a sensible focus trap entry.
    const t = setTimeout(() => closeBtnRef.current?.focus(), 80);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onScrimClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <>
      <style>{DRAWER_STYLES}</style>
      <div
        role="presentation"
        aria-hidden={!open}
        data-mobile-drawer-scrim
        data-open={open}
        onClick={onScrimClick}
        className={cn(
          "fixed inset-0 z-[var(--nx-z-modal)] bg-black/55 backdrop-blur-[6px] md:hidden",
          "transition-opacity duration-[250ms] ease-nx",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation drawer"
          data-mobile-drawer
          className={cn(
            "fixed inset-y-0 right-0 z-[calc(var(--nx-z-modal)+1)] flex h-full w-[86%] max-w-[420px] flex-col",
            "border-l border-nx-line-strong",
            "transition-transform duration-[350ms] ease-nx",
            "shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]",
            open ? "translate-x-0" : "translate-x-full",
          )}
          style={{
            background: "linear-gradient(180deg, #0E1018, #0A0B10)",
          }}
        >
          <header className="flex items-center gap-2.5 border-b border-nx-line px-4 py-3.5">
            <div className="flex-1">
              <Logo variant="horizontal" size="sm" />
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="grid h-9 w-9 place-items-center rounded-nx2 border border-nx-line-strong text-nx-text hover:bg-nx-surface-2"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </header>

          <div className="px-4 pt-3">
            <button
              type="button"
              data-mobile-drawer-search
              onClick={() => {
                onClose();
                // Open the command palette via the global Cmd-K listener.
                window.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "k", metaKey: true }),
                );
              }}
              className="flex w-full items-center gap-2.5 rounded-nx2 border border-nx-line bg-nx-surface/70 px-3 py-2.5 text-[13px] text-nx-text-dim hover:border-nx-line-strong"
              aria-label="Open search"
            >
              <SearchIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
              <span className="flex-1 text-left">Ask the assistant…</span>
            </button>
          </div>

          <nav
            aria-label="Mobile primary"
            className="flex-1 overflow-y-auto px-4 py-4 nx-drawer-scroll"
          >
            <div className="px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-nx-text-faint">
              Studio
            </div>
            <div className="flex flex-col gap-0.5">
              {NAV_ROWS.filter((r) => !r.hidden).map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={onClose}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-nx2 px-3 py-3",
                    "text-sm text-nx-text transition-colors hover:bg-nx-surface",
                  )}
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-nx-line bg-nx-surface text-nx-accent">
                    <r.icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </span>
                  <span className="flex flex-1 flex-col leading-tight">
                    <span className="text-[14.5px] text-nx-text">{r.label}</span>
                    <span className="font-mono text-[10.5px] text-nx-text-faint">
                      {r.helper}
                    </span>
                  </span>
                  {r.badge === "new" ? (
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-nx-accent-2 bg-nx-accent-2-soft border border-nx-accent-2/30 px-1.5 py-0.5 rounded-[4px]">
                      new
                    </span>
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-nx-text-faint" strokeWidth={2} />
                  )}
                </Link>
              ))}
            </div>

            <div className="mt-5 px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-nx-text-faint">
              Account
            </div>
            <div className="flex flex-col gap-0.5">
              {authed && me ? (
                <>
                  <Link
                    to="/portal"
                    onClick={onClose}
                    className="flex w-full items-center gap-3 rounded-nx2 px-3 py-3 text-sm text-nx-text hover:bg-nx-surface"
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-nx-line bg-nx-surface text-nx-accent">
                      <User className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span className="flex flex-1 flex-col leading-tight">
                      <span className="text-[14.5px] text-nx-text">Portal</span>
                      <span className="font-mono text-[10.5px] text-nx-text-faint truncate">
                        {me.label}
                      </span>
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-nx-text-faint" strokeWidth={2} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onSignOut();
                    }}
                    data-mobile-drawer-signout
                    className="flex w-full items-center gap-3 rounded-nx2 px-3 py-3 text-left text-sm text-nx-text hover:bg-nx-surface"
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-nx-line bg-nx-surface text-nx-text-dim">
                      <Key className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span className="flex flex-1 flex-col leading-tight">
                      <span className="text-[14.5px] text-nx-text">Sign out</span>
                      <span className="font-mono text-[10.5px] text-nx-text-faint">
                        Clear this session
                      </span>
                    </span>
                  </button>
                </>
              ) : (
                <Link
                  to="/portal/login"
                  onClick={onClose}
                  data-mobile-drawer-signin
                  className="flex w-full items-center gap-3 rounded-nx2 px-3 py-3 text-sm text-nx-text hover:bg-nx-surface"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-nx-line bg-nx-surface text-nx-accent">
                    <Key className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </span>
                  <span className="flex flex-1 flex-col leading-tight">
                    <span className="text-[14.5px] text-nx-text">Sign in</span>
                    <span className="font-mono text-[10.5px] text-nx-text-faint">
                      Access your portal
                    </span>
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-nx-text-faint" strokeWidth={2} />
                </Link>
              )}

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenChat();
                }}
                data-mobile-drawer-chat
                className="flex w-full items-center gap-3 rounded-nx2 px-3 py-3 text-left text-sm text-nx-text hover:bg-nx-surface"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] border border-nx-line bg-nx-surface text-nx-accent-2">
                  <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.8} />
                </span>
                <span className="flex flex-1 flex-col leading-tight">
                  <span className="text-[14.5px] text-nx-text">Message the team</span>
                  <span className="font-mono text-[10.5px] text-nx-text-faint">
                    Open NEXIRT Copilot
                  </span>
                </span>
              </button>
            </div>
          </nav>

          <footer className="border-t border-nx-line bg-nx-bg/50 px-4 py-4">
            <div className="flex items-center justify-between">
              <StatusPill />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint">
                v25 · phase 2
              </span>
            </div>
            <Link
              to="/contact"
              onClick={onClose}
              data-mobile-drawer-start
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-nx2 bg-nx-accent px-3.5 py-2.5 text-[14px] font-medium text-white shadow-[var(--nx-shadow-glow)] transition-[transform,filter] duration-150 ease-nx hover:-translate-y-[1px] hover:brightness-110"
            >
              <span>Start a project</span>
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </Link>
          </footer>
        </aside>
      </div>
    </>
  );
}

const DRAWER_STYLES = `
.nx-drawer-scroll::-webkit-scrollbar { width: 8px; }
.nx-drawer-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 8px; }
@media (prefers-reduced-motion: reduce) {
  [data-mobile-drawer], [data-mobile-drawer-scrim] {
    transition: none !important;
  }
}
`;

export default MobileDrawer;
